import { IDocumentAssemblerGeneration } from '@commenda/api';
import { Button, Icons, Tooltip } from '@commenda/kakashi';
import { AgentProfileHelper, copyToClipboard } from '@commenda/utils';
import moment from 'moment';
import Link from 'next/link';
import { useNotification } from '../hooks/useNotification';

import { DocumentAssemblerHelper } from './DocumentAssemblerHelper';

interface DocumentAssemblerGenerationProvenanceCardProps {
  generation: IDocumentAssemblerGeneration;
  versionUrl: string;
}

interface RecordRowProps {
  label: string;
  children: React.ReactNode;
}

const RecordRow = ({ label, children }: RecordRowProps) => (
  <div className="flex flex-col gap-xxs">
    <span className="txt-extra-small-medium text-text-tertiary">{label}</span>
    <div className="txt-small-regular text-text-primary">{children}</div>
  </div>
);

export const DocumentAssemblerGenerationProvenanceCard = ({
  generation,
  versionUrl,
}: DocumentAssemblerGenerationProvenanceCardProps) => {
  const { setNotification } = useNotification();

  const onRunIdCopy = () => {
    const copied = copyToClipboard(generation.agentRun?.id ?? '');

    setNotification({
      show: true,
      valid: copied,
      title: copied ? 'Run id copied' : 'Could not copy the run id',
    });
  };

  return (
    <div className="flex flex-col gap-lg rounded-xl border border-border-primary p-xl">
      <div className="grid gap-lg md:grid-cols-2">
        <RecordRow label="Version that produced it">
          <Link
            href={versionUrl}
            className="text-text-brand-primary hover:underline"
          >
            v{generation.templateVersion.version}
          </Link>
          <span className="ml-sm text-text-tertiary">
            {generation.templateVersion.status}
          </span>
        </RecordRow>
        <RecordRow label="The run behind it">
          {generation.agentRun ? (
            <div className="flex items-center gap-sm">
              <Tooltip
                content={
                  generation.agentRun.profileDescription ??
                  generation.agentRun.agentType
                }
              >
                <span>
                  {AgentProfileHelper.humaniseProfileKey(
                    generation.agentRun.agentType
                  )}
                </span>
              </Tooltip>
              {generation.agentRun.conversationId ? (
                <Link
                  href={`/agent/${generation.agentRun.conversationId}`}
                  className="text-text-brand-primary hover:underline"
                >
                  Open the run
                </Link>
              ) : (
                <Button
                  variant="link"
                  size="xs"
                  leftIcon={<Icons.Copy01 className="size-3" />}
                  onClick={onRunIdCopy}
                >
                  Copy run id
                </Button>
              )}
            </div>
          ) : (
            <span className="text-text-tertiary">No run recorded.</span>
          )}
        </RecordRow>
        <RecordRow label="Company">
          <Link
            href={`/company/${generation.companyId}`}
            className="text-text-brand-primary hover:underline"
          >
            {DocumentAssemblerHelper.describeCompany(
              generation.companyId,
              generation.companyName
            )}
          </Link>
        </RecordRow>
        <RecordRow label="Entity">
          {generation.corporationId === null ? (
            <span className="text-text-tertiary">—</span>
          ) : (
            <Link
              href={`/corporation/${generation.corporationId}`}
              className="text-text-brand-primary hover:underline"
            >
              {DocumentAssemblerHelper.describeCorporation(
                generation.corporationId,
                generation.corporationName
              )}
            </Link>
          )}
        </RecordRow>
        <RecordRow label="Started">
          {moment(generation.createdAt).format('lll')}
        </RecordRow>
        <RecordRow label="Finished">
          {generation.completedAt ? (
            moment(generation.completedAt).format('lll')
          ) : (
            <span className="text-text-tertiary">Not finished</span>
          )}
        </RecordRow>
      </div>
    </div>
  );
};
